// The games this guild plays, all equal (e.g. Classic Anniversary and WoW
// Forever). Server: auth/guild_game_routes.js. Officers only: add a game, edit
// a game's realm / faction / region, retire or reactivate it (history stays),
// or remove one that never had raids or members. Members add their characters
// from their Profile instead (Join Guild bar / "also add" at creation).
import React, { useCallback, useEffect, useState } from "react";
import { R } from "./theme/tokens";

type GuildGame = {
  gameId: string; gameName: string;
  serverName?: string | null; faction?: string | null; region?: string | null;
  isActive: boolean; raidCount: number;
};
type GamesResponse = { games: GuildGame[]; available: { gameId: string; gameName: string }[]; canManage: boolean };
type FieldOption = { id: number; label: string };
type FieldGroup = { key: string; options: FieldOption[] };

const ERRORS: Record<string, string> = {
  last_active_game: "A guild needs at least one active game.",
  game_has_raids: "This game has raids, so it can only be retired, not removed.",
  game_has_members: "Members have characters in this game. Retire it instead.",
  unknown_realm: "Pick a realm from the list.",
  unknown_faction: "Pick a faction from the list.",
  unknown_region: "Pick a region from the list.",
};

function shortName(n: string) {
  return n.replace(/^World of Warcraft:?\s*/i, "") || n;
}

export default function GuildGamesPanel({
  guildId,
  onGamesChanged,
}: {
  guildId: string;
  onGamesChanged?: () => void;
}) {
  const [data, setData] = useState<GamesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Add / edit form. editing = gameId being edited, "new" = adding.
  const [editing, setEditing] = useState<string | null>(null);
  const [gameId, setGameId] = useState("");
  const [fields, setFields] = useState<FieldGroup[]>([]);
  const [realm, setRealm] = useState("");
  const [faction, setFaction] = useState("");
  const [region, setRegion] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/guilds/${guildId}/games`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } catch {
      /* panel just stays hidden */
    }
  }, [guildId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setFields([]);
    if (!gameId) return;
    fetch(`/games/${encodeURIComponent(gameId)}/fields`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setFields)
      .catch(() => setFields([]));
  }, [gameId]);

  const opts = (key: string) => fields.find((g) => g.key === key)?.options || [];

  if (!data) return null;
  const multi = data.games.length > 1;
  // Officers only. Members add characters from their Profile (Join Guild bar,
  // or "also add to guild" when creating a character).
  if (!data.canManage) return null;

  const call = async (url: string, method: string, body?: any) => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(url, {
        method, credentials: "include",
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(ERRORS[j.error] || j.message || j.error || "Something went wrong");
      await load();
      onGamesChanged?.();
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const startAdd = () => { setEditing("new"); setGameId(""); setRealm(""); setFaction(""); setRegion(""); };
  const startEdit = (g: GuildGame) => {
    setEditing(g.gameId); setGameId(g.gameId);
    setRealm(g.serverName || ""); setFaction(g.faction || ""); setRegion(g.region || "");
  };
  const save = async () => {
    if (await call(`/guilds/${guildId}/games`, "POST", { gameId, serverName: realm, faction, region })) setEditing(null);
  };

  const select = "w-full rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2 text-sm text-[var(--c-ink)] outline-none focus:border-[var(--c-accent)]";

  const form = (
    <div className="grid gap-2 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 sm:grid-cols-4">
      {editing === "new" ? (
        <select className={select} value={gameId} onChange={(e) => { setGameId(e.target.value); setRealm(""); setFaction(""); setRegion(""); }}>
          <option value="">Game…</option>
          {data.available.map((g) => <option key={g.gameId} value={g.gameId}>{g.gameName}</option>)}
        </select>
      ) : (
        <div className="flex items-center px-1 text-sm font-bold text-[var(--c-ink)]">
          {shortName(data.games.find((g) => g.gameId === editing)?.gameName || "")}
        </div>
      )}
      <select className={select} value={region} onChange={(e) => setRegion(e.target.value)} disabled={!gameId}>
        <option value="">Region…</option>
        {opts("region").map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
      </select>
      <select className={select} value={realm} onChange={(e) => setRealm(e.target.value)} disabled={!gameId}>
        <option value="">Realm…</option>
        {opts("realm").map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
      </select>
      <select className={select} value={faction} onChange={(e) => setFaction(e.target.value)} disabled={!gameId}>
        <option value="">Faction…</option>
        {opts("faction").map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
      </select>
      <div className="flex gap-2 sm:col-span-4">
        <button onClick={save} disabled={busy || !gameId || !realm || !faction || !region} className={R.btnPrimary + " px-4 py-2 text-xs"}>
          {editing === "new" ? "Add game" : "Save"}
        </button>
        <button onClick={() => setEditing(null)} className={R.btnGhost + " text-xs"}>Cancel</button>
      </div>
    </div>
  );

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)]">Games</h3>

      <div className="grid gap-2 sm:grid-cols-2">
        {data.games.map((g) => (
          <div key={g.gameId} className={`flex items-center gap-3 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3 ${g.isActive ? "" : "opacity-70"}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-[var(--c-ink)]">{shortName(g.gameName)}</span>
                {!g.isActive && <span className={R.pill}>Retired</span>}
              </div>
              <div className="text-xs text-[var(--c-ink-3)]">
                {[g.serverName, g.faction, g.region].filter(Boolean).join(" · ") || "·"}
                {g.raidCount > 0 && <span> · {g.raidCount} raid{g.raidCount === 1 ? "" : "s"}</span>}
              </div>
            </div>
            {data.canManage && (
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => startEdit(g)} disabled={busy} className={R.btnGhost + " text-xs"}>Edit</button>
                {multi && (
                  <button
                    onClick={() => call(`/guilds/${guildId}/games/${g.gameId}`, "PATCH", { isActive: !g.isActive })}
                    disabled={busy}
                    title={g.isActive ? "No new raids or members in this game. History stays." : "Make this game active again"}
                    className={R.btnGhost + " text-xs"}
                  >
                    {g.isActive ? "Retire" : "Reactivate"}
                  </button>
                )}
                {multi && g.raidCount === 0 && (
                  <button onClick={() => call(`/guilds/${guildId}/games/${g.gameId}`, "DELETE")} disabled={busy} className={R.btnGhost + " text-xs"}>
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {data.canManage && (editing ? form : data.available.length > 0 && (
        <button onClick={startAdd} className={R.btnSecondary + " px-4 py-2 text-xs"}>+ Add a game</button>
      ))}

      {error && <p className="text-xs text-[var(--c-bad-ink)]">{error}</p>}
    </section>
  );
}
